export type ColorTheme = "light" | "dark";

export const THEME_STORAGE_KEY = "nlkb.theme";
export const THEME_COLOR_LIGHT = "#f3eee4";
export const THEME_COLOR_DARK = "#171412";
export const DEFAULT_COLOR_THEME: ColorTheme = "dark";

const themeListeners = new Set<(theme: ColorTheme) => void>();

export function parseColorTheme(value: string | null): ColorTheme | null {
  return value === "light" || value === "dark" ? value : null;
}

export function readStoredColorTheme(): ColorTheme | null {
  try {
    return parseColorTheme(window.localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    return null;
  }
}

export function persistColorTheme(theme: ColorTheme): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Ignore quota and private-mode failures; the document theme still applies.
  }
}

export function resolveColorTheme(stored: ColorTheme | null): ColorTheme {
  return stored ?? DEFAULT_COLOR_THEME;
}

export function applyColorTheme(
  theme: ColorTheme,
  root = document.documentElement,
): void {
  root.dataset.theme = theme;
  root.style.colorScheme = theme;
  const meta = root.ownerDocument.querySelector('meta[name="theme-color"]');
  if (meta !== null) {
    meta.setAttribute(
      "content",
      theme === "dark" ? THEME_COLOR_DARK : THEME_COLOR_LIGHT,
    );
  }
}

export function applyResolvedColorTheme(): ColorTheme {
  const theme = resolveColorTheme(readStoredColorTheme());
  applyColorTheme(theme);
  return theme;
}

export function selectColorTheme(theme: ColorTheme): void {
  persistColorTheme(theme);
  applyColorTheme(theme);
  for (const listener of themeListeners) {
    listener(theme);
  }
}

export function oppositeColorTheme(theme: ColorTheme): ColorTheme {
  return theme === "dark" ? "light" : "dark";
}

export function watchColorTheme(
  onTheme: (theme: ColorTheme) => void,
): () => void {
  themeListeners.add(onTheme);
  const onStorage = () => {
    onTheme(applyResolvedColorTheme());
  };
  window.addEventListener("storage", onStorage);
  return () => {
    themeListeners.delete(onTheme);
    window.removeEventListener("storage", onStorage);
  };
}
